import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, Package, Download, Edit, Eye, EyeOff, ChevronDown, ChevronUp, Check, X, Truck } from "lucide-react";

export default function AdminReflectionLibrary() {
  const [tab, setTab] = useState("products");
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, o] = await Promise.all([
        http.get("/reflection-library/admin/settings"),
        http.get("/reflection-library/admin/products"),
        http.get("/reflection-library/admin/orders"),
      ]);
      setSettings(s.data);
      setProducts(p.data);
      setOrders(o.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async (updates) => {
    try {
      const { data } = await http.patch("/reflection-library/admin/settings", updates);
      setSettings(data);
      toast.success("Saved");
    } catch { toast.error("Failed to save"); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const tabs = [
    { key: "products", label: "Products", count: products.length },
    { key: "orders", label: "Orders", count: orders.length },
    { key: "settings", label: "Settings" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-reflection-library">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display text-3xl text-[#F4F1EA]">Reflection Library&trade;</h2>
          <p className="font-body text-[13px] text-[#8a857d] mt-1">Manage worksheets, orders, and library settings.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-[#8a857d] uppercase tracking-wider">Library</span>
          <Toggle checked={settings?.enabled !== false} onChange={v => saveSettings({ enabled: v })} />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`rl-tab-${t.key}`}
            className={`px-4 py-2 text-xs font-ui tracking-[0.15em] uppercase border transition-colors ${
              tab === t.key ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/10 text-[#8a857d] hover:border-[#C9A055]/50"
            }`}>
            {t.label} {t.count !== undefined && <span className="ml-1 opacity-60">({t.count})</span>}
          </button>
        ))}
      </div>

      {tab === "products" && <ProductsTab products={products} allProducts={products} settings={settings} onReload={load} editProduct={editProduct} setEditProduct={setEditProduct} />}
      {tab === "orders" && <OrdersTab orders={orders} products={products} onReload={load} />}
      {tab === "settings" && <SettingsTab settings={settings} saveSettings={saveSettings} />}
    </div>
  );
}

/* ═══ PRODUCTS TAB ═══ */
function ProductsTab({ products, allProducts, settings, onReload, editProduct, setEditProduct }) {
  const blank = { title: "", slug: "", short_desc: "", full_desc: "", who_for: "", whats_included: "", how_to_use: "", thumbnail_url: "", pdf_url: "", digital_price: 0, print_price: 0, bundle_price: 0, categories: [], tags: [], available_as: "both", is_bundle: false, bundle_items: [], active: true, sort_order: 0, related_ids: [] };

  const saveProduct = async (data) => {
    try {
      if (data.id) {
        await http.patch(`/reflection-library/admin/products/${data.id}`, data);
        toast.success("Product updated");
      } else {
        await http.post("/reflection-library/admin/products", data);
        toast.success("Product created");
      }
      setEditProduct(null);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await http.delete(`/reflection-library/admin/products/${id}`);
      toast.success("Deleted");
      onReload();
    } catch { toast.error("Failed to delete"); }
  };

  if (editProduct) return <ProductForm product={editProduct} allProducts={allProducts} settings={settings} onSave={saveProduct} onCancel={() => setEditProduct(null)} />;

  return (
    <div className="space-y-3">
      <button onClick={() => setEditProduct({ ...blank })} className="btn-gold text-xs" data-testid="add-product"><Plus size={14} /> Add Worksheet</button>
      {products.length === 0 && <div className="p-10 text-center text-[#8a857d] text-sm border border-white/5 bg-[#0a0a0a]">No products yet. Add your first worksheet.</div>}
      {products.map(p => (
        <div key={p.id} className="bg-[#0a0a0a] border border-white/10 p-4 flex gap-4 items-start" data-testid={`product-row-${p.id}`}>
          <div className="w-16 h-16 rounded bg-[#111] shrink-0 overflow-hidden">
            {p.thumbnail_url ? <img src={p.thumbnail_url.startsWith("http") ? p.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${p.thumbnail_url}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#5a5650] text-[10px]">none</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-ui text-sm text-[#F4F1EA] font-bold truncate">{p.title}</h3>
              {!p.active && <span className="text-[9px] bg-red-900/30 text-red-400 px-1.5 py-0.5">HIDDEN</span>}
              {p.is_bundle && <span className="text-[9px] bg-[#D4AF37]/20 text-[#D4AF37] px-1.5 py-0.5">BUNDLE</span>}
            </div>
            <p className="text-xs text-[#8a857d] truncate">{p.short_desc}</p>
            <div className="flex gap-3 mt-1 text-[10px] text-[#8a857d]">
              {p.digital_price > 0 && <span><Download size={10} className="inline" /> ${p.digital_price}</span>}
              {p.print_price > 0 && <span><Package size={10} className="inline" /> ${p.print_price}</span>}
              {p.bundle_price > 0 && <span>Both ${p.bundle_price}</span>}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => setEditProduct({ ...p })} className="btn-outline px-2"><Edit size={12} /></button>
            <button onClick={() => deleteProduct(p.id)} className="btn-outline px-2 border-red-900/50 text-red-500"><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══ PRODUCT FORM ═══ */
function ProductForm({ product, allProducts, settings, onSave, onCancel }) {
  const [f, setF] = useState({ ...product });
  const [tagInput, setTagInput] = useState("");
  const availableCategories = (settings?.categories || []).filter(c => c !== "All" && c !== "Bundles");

  const addTag = () => { if (tagInput.trim()) { setF({ ...f, tags: [...(f.tags || []), tagInput.trim()] }); setTagInput(""); } };
  const toggleCategory = (cat) => {
    const cats = f.categories || [];
    if (cats.includes(cat)) {
      setF({ ...f, categories: cats.filter(c => c !== cat) });
    } else {
      setF({ ...f, categories: [...cats, cat] });
    }
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-6 space-y-4" data-testid="product-form">
      <div className="flex justify-between items-center">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">{f.id ? "Edit" : "New"} Worksheet</h3>
        <button onClick={onCancel} className="text-[#8a857d] hover:text-white"><X size={16} /></button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Field label="Title" value={f.title} onChange={v => setF({ ...f, title: v })} />
        <Field label="Slug (auto-generated if blank)" value={f.slug} onChange={v => setF({ ...f, slug: v })} />
      </div>
      <Field label="Short Description" value={f.short_desc} onChange={v => setF({ ...f, short_desc: v })} textarea rows={2} />
      <Field label="Full Description" value={f.full_desc} onChange={v => setF({ ...f, full_desc: v })} textarea rows={4} />
      <div className="grid lg:grid-cols-3 gap-4">
        <Field label="Who this is for" value={f.who_for} onChange={v => setF({ ...f, who_for: v })} textarea rows={3} />
        <Field label="What's included" value={f.whats_included} onChange={v => setF({ ...f, whats_included: v })} textarea rows={3} />
        <Field label="How to use it" value={f.how_to_use} onChange={v => setF({ ...f, how_to_use: v })} textarea rows={3} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Uploader label="Thumbnail Image" url={f.thumbnail_url} onUpload={url => setF({ ...f, thumbnail_url: url })} />
        <Uploader label="Digital PDF File" url={f.pdf_url} onUpload={url => setF({ ...f, pdf_url: url })} accept=".pdf,application/pdf" />
      </div>

      <div className="grid grid-cols-4 gap-4">
        <PriceField label="Digital Price" value={f.digital_price} onChange={v => setF({ ...f, digital_price: v })} />
        <PriceField label="Print Price" value={f.print_price} onChange={v => setF({ ...f, print_price: v })} />
        <PriceField label="Bundle Price (Both)" value={f.bundle_price} onChange={v => setF({ ...f, bundle_price: v })} />
        <div>
          <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Available As</label>
          <select value={f.available_as} onChange={e => setF({ ...f, available_as: e.target.value })} className="stc-input w-full text-sm">
            <option value="digital">Digital Only</option>
            <option value="print">Print Only</option>
            <option value="both">Digital + Print</option>
          </select>
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Tags</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {f.tags?.map((t, i) => (
            <span key={i} className="text-[10px] bg-white/5 text-[#8a857d] px-2 py-0.5 border border-white/10 flex items-center gap-1">
              {t} <button onClick={() => setF({ ...f, tags: f.tags.filter((_, j) => j !== i) })}><X size={10} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Add tag..." className="stc-input flex-1 text-sm" />
          <button onClick={addTag} className="btn-outline text-xs px-3">Add</button>
        </div>
      </div>

      {/* Categories - Checkbox picker */}
      <div>
        <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-2">Categories</label>
        <div className="flex flex-wrap gap-2">
          {availableCategories.map(cat => {
            const selected = (f.categories || []).includes(cat);
            return (
              <button key={cat} type="button" onClick={() => toggleCategory(cat)} data-testid={`cat-toggle-${cat}`}
                className={`text-xs px-3 py-1.5 border transition-colors ${
                  selected
                    ? "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/40"
                    : "bg-transparent text-[#8a857d] border-white/10 hover:border-[#D4AF37]/30"
                }`}>
                {selected && <span className="mr-1">&#10003;</span>}{cat}
              </button>
            );
          })}
        </div>
        {(f.categories || []).length > 0 && (
          <p className="text-[10px] text-[#8a857d] mt-1">Selected: {f.categories.join(", ")}</p>
        )}
      </div>

      <div className="flex gap-4 items-center">
        <label className="flex items-center gap-2 text-xs text-[#8a857d]"><input type="checkbox" checked={f.is_bundle} onChange={e => setF({ ...f, is_bundle: e.target.checked })} className="accent-[#C9A055]" /> This is a Bundle</label>
        <label className="flex items-center gap-2 text-xs text-[#8a857d]"><input type="checkbox" checked={f.active} onChange={e => setF({ ...f, active: e.target.checked })} className="accent-[#C9A055]" /> Active / Visible</label>
        <div className="flex items-center gap-2 text-xs text-[#8a857d]">Sort: <input type="number" value={f.sort_order} onChange={e => setF({ ...f, sort_order: parseInt(e.target.value) || 0 })} className="stc-input w-16 text-sm" /></div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="btn-outline text-xs px-4">Cancel</button>
        <button onClick={() => onSave(f)} className="btn-gold text-xs" data-testid="save-product"><Save size={12} /> {f.id ? "Update" : "Create"} Worksheet</button>
      </div>
    </div>
  );
}

/* ═══ ORDERS TAB ═══ */
function OrdersTab({ orders, products, onReload }) {
  const updateOrder = async (id, status) => {
    try {
      await http.patch(`/reflection-library/admin/orders/${id}`, { status });
      toast.success(`Order marked ${status}`);
      onReload();
    } catch { toast.error("Failed to update"); }
  };

  if (!orders.length) return <div className="p-10 text-center text-[#8a857d] text-sm border border-white/5 bg-[#0a0a0a]">No orders yet.</div>;

  return (
    <div className="space-y-3">
      {orders.map((o, i) => (
        <div key={o.id} className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3" data-testid={`order-row-${i}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="font-ui text-sm text-[#F4F1EA] font-bold">{o.customer_name}</h3>
              <span className="text-xs text-[#8a857d]">{o.customer_email}</span>
            </div>
            <div className="text-right">
              <StatusBadge status={o.status} />
              <div className="text-[10px] text-[#5a5650] mt-1">{fmtDate(o.created_at)}</div>
            </div>
          </div>
          <div className="text-xs text-[#8a857d] space-y-1">
            {o.items?.map((item, j) => (
              <div key={j}>{item.product_id} — {item.format}</div>
            ))}
          </div>
          {o.has_print && o.shipping_address && (
            <div className="text-xs text-[#8a857d] bg-white/[0.02] p-3 border border-white/5">
              <span className="text-[#C9A055] uppercase tracking-wider text-[10px]">Ship to:</span>
              <div>{o.shipping_address.line1}</div>
              {o.shipping_address.line2 && <div>{o.shipping_address.line2}</div>}
              <div>{o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.zip}</div>
              {o.shipping_cost > 0 && <div className="mt-1">Shipping: ${o.shipping_cost}</div>}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            {o.status === "pending" && <button onClick={() => updateOrder(o.id, "paid")} className="btn-outline text-xs px-3"><Check size={12} /> Mark Paid</button>}
            {(o.status === "paid" && o.has_print) && <button onClick={() => updateOrder(o.id, "shipped")} className="btn-outline text-xs px-3"><Truck size={12} /> Mark Shipped</button>}
            {o.status === "shipped" && <button onClick={() => updateOrder(o.id, "delivered")} className="btn-outline text-xs px-3"><Check size={12} /> Mark Delivered</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══ SETTINGS TAB ═══ */
function SettingsTab({ settings, saveSettings }) {
  const [s, setS] = useState(settings);
  const [rates, setRates] = useState(settings.shipping_rates || []);
  const [cats, setCats] = useState(settings.categories || []);
  const [catInput, setCatInput] = useState("");

  return (
    <div className="space-y-6">
      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">Hero Section</h3>
        <Field label="Title" value={s.hero_title || ""} onChange={v => setS({ ...s, hero_title: v })} />
        <Field label="Subtitle" value={s.hero_subtitle || ""} onChange={v => setS({ ...s, hero_subtitle: v })} />
        <Field label="Text" value={s.hero_text || ""} onChange={v => setS({ ...s, hero_text: v })} textarea rows={3} />
        <Field label="CTA Text" value={s.cta_text || ""} onChange={v => setS({ ...s, cta_text: v })} />
        <Field label="CTA Link" value={s.cta_link || ""} onChange={v => setS({ ...s, cta_link: v })} />
        <div className="flex justify-end">
          <button onClick={() => saveSettings({ hero_title: s.hero_title, hero_subtitle: s.hero_subtitle, hero_text: s.hero_text, cta_text: s.cta_text, cta_link: s.cta_link })} className="btn-gold text-[10px]"><Save size={12} /> Save Hero</button>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">First-Class Shipping Rates</h3>
        {rates.map((rate, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input value={rate.label} onChange={e => { const r = [...rates]; r[i] = { ...r[i], label: e.target.value }; setRates(r); }} className="stc-input flex-1 text-sm" placeholder="Label" />
            <input type="number" value={rate.min_sheets} onChange={e => { const r = [...rates]; r[i] = { ...r[i], min_sheets: parseInt(e.target.value) || 0 }; setRates(r); }} className="stc-input w-20 text-sm" placeholder="Min" />
            <input type="number" value={rate.max_sheets} onChange={e => { const r = [...rates]; r[i] = { ...r[i], max_sheets: parseInt(e.target.value) || 0 }; setRates(r); }} className="stc-input w-20 text-sm" placeholder="Max" />
            <span className="text-[#8a857d]">$</span>
            <input type="number" step="0.01" value={rate.price} onChange={e => { const r = [...rates]; r[i] = { ...r[i], price: parseFloat(e.target.value) || 0 }; setRates(r); }} className="stc-input w-20 text-sm" />
            <button onClick={() => setRates(rates.filter((_, j) => j !== i))} className="btn-outline px-1.5 border-red-900/50 text-red-500"><Trash2 size={12} /></button>
          </div>
        ))}
        <button onClick={() => setRates([...rates, { min_sheets: 0, max_sheets: 0, price: 0, label: "" }])} className="btn-outline text-xs w-full justify-center"><Plus size={14} /> Add Rate</button>
        <div className="flex justify-end">
          <button onClick={() => saveSettings({ shipping_rates: rates })} className="btn-gold text-[10px]"><Save size={12} /> Save Rates</button>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-3">
        <h3 className="font-ui text-[12px] tracking-[0.2em] text-[#C9A055] uppercase">Filter Categories</h3>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {cats.map((c, i) => (
            <span key={i} className="text-[10px] bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 border border-[#D4AF37]/20 flex items-center gap-1">
              {c} <button onClick={() => setCats(cats.filter((_, j) => j !== i))}><X size={10} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={catInput} onChange={e => setCatInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), catInput.trim() && (setCats([...cats, catInput.trim()]), setCatInput("")))} className="stc-input flex-1 text-sm" placeholder="Add category..." />
          <button onClick={() => { if (catInput.trim()) { setCats([...cats, catInput.trim()]); setCatInput(""); } }} className="btn-outline text-xs px-3">Add</button>
        </div>
        <div className="flex justify-end">
          <button onClick={() => saveSettings({ categories: cats })} className="btn-gold text-[10px]"><Save size={12} /> Save Categories</button>
        </div>
      </div>
    </div>
  );
}

/* ═══ SHARED ═══ */
function Toggle({ checked, onChange }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only peer" />
      <div className="w-10 h-5 bg-zinc-700 rounded-full peer peer-checked:bg-[#D4AF37] transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5" />
    </label>
  );
}

function Field({ label, value, onChange, textarea, rows = 3 }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">{label}</label>
      <Tag value={value || ""} onChange={e => onChange(e.target.value)} className="stc-input w-full" {...(textarea ? { rows } : {})} />
    </div>
  );
}

function PriceField({ label, value, onChange }) {
  return (
    <div>
      <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">{label}</label>
      <div className="flex items-center gap-1"><span className="text-[#8a857d]">$</span>
        <input type="number" step="0.01" value={value || 0} onChange={e => onChange(parseFloat(e.target.value) || 0)} className="stc-input w-full text-sm" />
      </div>
    </div>
  );
}

function Uploader({ label, url, onUpload, accept = "image/*" }) {
  return (
    <div>
      <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1 uppercase">{label}</div>
      <div className="flex items-center gap-3 flex-wrap">
        {url ? (
          accept.includes("pdf") ? <span className="text-xs text-[#C9A055]">PDF uploaded</span>
            : <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover border border-white/10 rounded" />
        ) : <div className="w-16 h-16 border border-white/10 bg-black flex items-center justify-center text-[#5a5650] text-[10px] rounded">none</div>}
        <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.22em] uppercase cursor-pointer">
          <input type="file" accept={accept} className="hidden" onChange={e => {
            const file = e.target.files?.[0]; if (!file) return;
            const reader = new FileReader();
            reader.onload = async () => {
              try {
                const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: file.name });
                onUpload(data.url); toast.success("Uploaded");
              } catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
            };
            reader.readAsDataURL(file);
          }} />
          Upload
        </label>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = { pending: "bg-yellow-900/30 text-yellow-400 border-yellow-800/50", paid: "bg-green-900/30 text-green-400 border-green-800/50", shipped: "bg-blue-900/30 text-blue-400 border-blue-800/50", delivered: "bg-zinc-800 text-zinc-400 border-zinc-700" };
  return <div className={`inline-block text-[10px] tracking-wider uppercase px-2 py-0.5 border ${colors[status] || colors.pending}`}>{status}</div>;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
